/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../buttons/Button";
import {
  errorAlert,
  makeApiRequest,
  successAlert,
  transformDFSIConnectionResponseRow,
} from "../../../util";
import AuthContext from "../../../store/authContext";
import { ADD_DFSI_CHANNEL_FORM_FIELDS } from "../../../constants";
import Form from "../../../ui/Form/Form";
import NumberField from "../../inputs/NumberField";
import TextField from "../../inputs/TextField";
import { IDFSIChannelData } from "../../../types";
import DeleteModal, { DeleteModalIcons } from "../../modal/DeleteModal";
import HexInput from "../../../ui/HexInput";
import DropdownField from "../../inputs/DropdownField";
import DropdownSearchField from "../../inputs/DropdownSearchField";
import {
  CREATE_DFSI_CHANNEL_TRANSLATIONS,
  FORM_VALIDATION_TRANSLATIONS,
} from "../../../translations/util";
import CheckBox from "../../checkbox/CheckBox";

interface IProps {
  org: string;
}
const set = new Set();

const isFormInvalid = ({ data }: { data: any }) => {
  if (!data) return true;
  const isConnectionFormInvalid = ADD_DFSI_CHANNEL_FORM_FIELDS.map(
    (rows: any) => {
      const res = rows.fields.map((field: any) => {
        switch (field.type) {
          case "TEXT":
            return field.isInputInvalid?.(data[field.name]);
          case "CHECKBOX":
            return field.isInputInvalid?.(
              data["CHANNEL-MODE"],
              data["DFSI-VERSION"]
            );
          case "NUMBER":
            return field.isInputInvalid?.(
              data["CHANNEL-MODE"],
              data[field.name]
            );
          case "HEX":
            return field.isInputInvalid?.(
              data["CHANNEL-MODE"],
              data[field.name]
            );
          case "DROPDOWN":
            return field.isInputInvalid?.(data[field.name]);
          case "CUSTOM-DROPDOWN":
            return field.isInputInvalid?.(data[field.name]);
          case "COMBO-DROPDOWN":
            return field.isInputInvalid?.(
              data["CHANNEL-MODE"],
              data[field.name]
            );
          default:
            return false;
        }
      });

      return res.reduce((acc: any, el: any) => acc || el);
    }
  ).reduce((acc: any, el: any) => acc || el);

  return isConnectionFormInvalid;
};

const NewDFSIChannel = ({ org }: IProps) => {
  const { token } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [connectionIds, setConnectionIds] = useState<string[]>([]);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    set.clear();
    return () => {
      set.clear();
    };
  }, []);

  const [connectionData, setConnectionData] = useState<IDFSIChannelData>({
    "CONNECTION-ID": "",
    "CHANNEL-ID": "",
    "DFSI-VERSION": undefined,
    "OUTBOUND-NETWORK-ACCESS-CODE": 659,
    "INBOUND-NETWORK-ACCESS-CODE": 659,
    "OTA-GROUP-ID": undefined,
    "CHANNEL-MODE": "",
    "CARRIER-SQUELCH": false,
    "CTC-SQUELCH-SYSTEM": "",
    "CDC-SQUELCH-SYSTEM": "",
  });

  const [isChecked, setIsChecked] = useState(false);

  const handleCTCnCDCvalues = (
    field?: string,
    ctcvalue?: string,
    cdcValue?: string,
    isChecked?: boolean
  ) => {
    if (ctcvalue === "None" && cdcValue === "None" && !isChecked) return 1;
    if (isChecked) return 1;
    if (field === "ctc" && cdcValue === "None" && ctcvalue !== "None") return 1;
    if (field === "cdc" && ctcvalue === "None" && cdcValue !== "None") return 1;
    return null;
  };

  const createDFSIChannels = async () => {
    setLoading(true);
    try {
      await makeApiRequest<{ Ref: string }>({
        path: `/api/v1/app-config/dfsi-channels`,
        method: "POST",
        body: JSON.stringify({
          "CONNECTION-ID": connectionData["CONNECTION-ID"],
          "CHANNEL-ID": connectionData["CHANNEL-ID"],
          "CHANNEL-MODE": connectionData["CHANNEL-MODE"],
          ...(connectionData["CHANNEL-MODE"] &&
            connectionData["CHANNEL-MODE"] === "Analog" &&
            connectionData["DFSI-VERSION"] === 2 &&
            handleCTCnCDCvalues(
              "ctc",
              connectionData["CTC-SQUELCH-SYSTEM"],
              connectionData["CDC-SQUELCH-SYSTEM"],
              isChecked
            ) && {
              "CTC-SQUELCH-SYSTEM":
                isChecked ||
                (connectionData["CTC-SQUELCH-SYSTEM"] === "None" &&
                  connectionData["CDC-SQUELCH-SYSTEM"] === "None")
                  ? 0
                  : connectionData["CTC-SQUELCH-SYSTEM"] &&
                    +connectionData["CTC-SQUELCH-SYSTEM"],
            }),
          ...(connectionData["CHANNEL-MODE"] &&
            connectionData["CHANNEL-MODE"] === "Digital" && {
              "OTA-GROUP-ID":
                connectionData["OTA-GROUP-ID"] &&
                +connectionData["OTA-GROUP-ID"],
            }),
          ...(connectionData["CHANNEL-MODE"] &&
            connectionData["CHANNEL-MODE"] === "Digital" && {
              "OUTBOUND-NETWORK-ACCESS-CODE":
                connectionData["OUTBOUND-NETWORK-ACCESS-CODE"] &&
                +connectionData["OUTBOUND-NETWORK-ACCESS-CODE"],
            }),
          ...(connectionData["CHANNEL-MODE"] &&
            connectionData["CHANNEL-MODE"] === "Digital" && {
              "INBOUND-NETWORK-ACCESS-CODE":
                connectionData["INBOUND-NETWORK-ACCESS-CODE"] &&
                +connectionData["INBOUND-NETWORK-ACCESS-CODE"],
            }),
          ...(connectionData["CHANNEL-MODE"] &&
            connectionData["CHANNEL-MODE"] === "Analog" &&
            connectionData["DFSI-VERSION"] === 2 &&
            handleCTCnCDCvalues(
              "cdc",
              connectionData["CTC-SQUELCH-SYSTEM"],
              connectionData["CDC-SQUELCH-SYSTEM"],
              isChecked
            ) && {
              "CDC-SQUELCH-SYSTEM":
                isChecked ||
                (connectionData["CTC-SQUELCH-SYSTEM"] === "None" &&
                  connectionData["CDC-SQUELCH-SYSTEM"] === "None")
                  ? 0
                  : connectionData["CDC-SQUELCH-SYSTEM"] &&
                    +connectionData["CDC-SQUELCH-SYSTEM"],
            }),
        }),
        token,
        org,
      });
      successAlert(CREATE_DFSI_CHANNEL_TRANSLATIONS.successAlertMessage);
      navigate("/configurationManagement/dfsiChannels");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      try {
        errorAlert(e);
      } catch (err) {
        errorAlert(e.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const getDFSIConectionIds = async (orgId: string) => {
    try {
      const response = await makeApiRequest<{
        Rows: { "CONNECTION-ID": string }[];
      }>({
        path: `/api/v1/app-config/dfsi-connections/ids/`,
        token,
        org: orgId,
      });
      setConnectionIds(response.Rows.map((row) => row["CONNECTION-ID"]));
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    }
  };

  const handleFetchDFSIVersion = async (connectionID: string) => {
    if (connectionID) {
      try {
        const queryParams = new URLSearchParams({
          page: "1",
          pageSize: "20",
          ...(connectionID && {
            "CONNECTION-ID": connectionID,
          }),
        });
        const response = await makeApiRequest<{
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          Rows: Array<any>;
          NbrOfRows: number;
          NbrOfPages: number;
        }>({
          path: `/api/v1/app-config/dfsi-connections?${queryParams}`,
          token,
          org,
        });
        const dfsiConnectionData = transformDFSIConnectionResponseRow(
          response.Rows[0]
        );
        setConnectionData({
          ...connectionData,
          "CONNECTION-ID": connectionID,
          "DFSI-VERSION": dfsiConnectionData["DFSI-VERSION"],
        });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        errorAlert(e);
      }
    }
  };
  useEffect(() => {
    getDFSIConectionIds(org);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org]);

  return (
    <>
      <div className="data-form">
        <Form>
          <div className="data-tabcontent data-card">
            {ADD_DFSI_CHANNEL_FORM_FIELDS.map(
              (rows: {
                cols: number;
                fields: {
                  name: keyof IDFSIChannelData;
                  tooltipText?: string;
                  label: string;
                  type: string;
                  invalidText?: string | null;
                  isInputInvalid?: (
                    chanelMode?: string,
                    val?: string | number
                  ) => boolean;
                  min?: number;
                  max?: number;
                  items?: string[];
                  comboItems: ({
                    fieldName,
                    dfsiVersion,
                    channelMode,
                    ctcValue,
                    cdcValue,
                  }: {
                    fieldName?: string;
                    dfsiVersion?: number;
                    channelMode?: string;
                    ctcValue?: string;
                    cdcValue?: string;
                  }) => string[];
                  disabled?: (
                    chanelMode?: string,
                    dfsiVersion?: number,
                    isChecked?: boolean
                  ) => boolean;
                }[];
              }) => {
                return (
                  <div
                    className={`grid grid-cols-${rows.cols} gap-4 form-control single-item `}
                  >
                    {rows.fields.map((field) => {
                      switch (field.type) {
                        case "NUMBER":
                          return (
                            <NumberField
                              onChange={(val) => {
                                set.add(field.name);
                                if (!val || !Number.isNaN(+val))
                                  setConnectionData({
                                    ...connectionData,
                                    [field.name]: val ? +val : undefined,
                                  });
                              }}
                              label={field.label}
                              placeholder={field.label}
                              max={field.max || 0}
                              min={field.min || 0}
                              invalid={
                                set.has(field.name) &&
                                connectionData[field.name] !== 0 &&
                                !connectionData[field.name]
                              }
                              value={connectionData[field.name] as number}
                              invalidText={
                                field.invalidText ||
                                `"${field.label}" ${FORM_VALIDATION_TRANSLATIONS.range} (${field.min}-${field.max})`
                              }
                              tooltipText={field.tooltipText}
                              required
                              disabled={field.disabled?.(
                                connectionData["CHANNEL-MODE"]
                              )}
                            />
                          );
                        case "HEX":
                          return (
                            <HexInput
                              onChange={(val) => {
                                set.add(field.name);
                                setConnectionData({
                                  ...connectionData,
                                  [field.name]:
                                    val || val === 0 ? +val : undefined,
                                });
                              }}
                              label={field.label}
                              placeholder={field.label}
                              max={field.max || 0}
                              min={field.min || 0}
                              invalid={
                                set.has(field.name) &&
                                field.isInputInvalid?.(
                                  connectionData["CHANNEL-MODE"],
                                  connectionData[field.name] as number
                                )
                              }
                              value={connectionData[field.name] as number}
                              invalidText={
                                field.invalidText ||
                                `"${field.label}" ${FORM_VALIDATION_TRANSLATIONS.range} (${field.min}-${field.max})`
                              }
                              tooltipText={field.tooltipText}
                              required
                              disabled={field.disabled?.(
                                connectionData["CHANNEL-MODE"]
                              )}
                            />
                          );
                        case "CHECKBOX":
                          return (
                            <div className="dfsi-channel-checkbox">
                              <CheckBox
                                labelText={field.label}
                                id={field.label}
                                disabled={field.disabled?.(
                                  connectionData["CHANNEL-MODE"],
                                  connectionData["DFSI-VERSION"]
                                )}
                                invalid={
                                  set.has(field.name) &&
                                  field.isInputInvalid?.(
                                    connectionData["CHANNEL-MODE"],
                                    connectionData["DFSI-VERSION"]
                                  )
                                }
                                invalidText={
                                  field.invalidText ||
                                  `"${field.label}" ${FORM_VALIDATION_TRANSLATIONS.range} (${field.min}-${field.max})`
                                }
                                onChange={(e) => {
                                  set.add(field.name);
                                  setIsChecked(!isChecked);
                                  setConnectionData({
                                    ...connectionData,
                                    [field.name]: e.target.checked,
                                    "CTC-SQUELCH-SYSTEM": "None",
                                    "CDC-SQUELCH-SYSTEM": "None",
                                  });
                                }}
                                checked={false}
                              />
                            </div>
                          );
                        case "DROPDOWN":
                          return (
                            <DropdownField
                              className="dropdown-field"
                              label={field.label}
                              selectedItem={connectionData[field.name]}
                              items={field.items || []}
                              onChange={({
                                selectedItem,
                              }: {
                                selectedItem: string;
                              }) =>
                                setConnectionData({
                                  ...connectionData,
                                  [field.name]: selectedItem,
                                })
                              }
                              tooltipText={field.tooltipText}
                              required
                            />
                          );
                        case "CUSTOM-DROPDOWN":
                          return (
                            <DropdownSearchField
                              className="dropdown-field"
                              label={field.label}
                              placeholder={field.label}
                              items={connectionIds}
                              onChange={({
                                selectedItem,
                              }: {
                                selectedItem: string;
                              }) =>
                                selectedItem
                                  ? handleFetchDFSIVersion(selectedItem)
                                  : undefined
                              }
                              selectedItem={connectionData[field.name]}
                              tooltipText={field.tooltipText}
                              required
                            />
                          );
                        case "COMBO-DROPDOWN":
                          return (
                            <DropdownSearchField
                              className="dropdown-field"
                              label={field.label}
                              placeholder={field.label}
                              items={
                                field.comboItems({
                                  fieldName: field.name,
                                  dfsiVersion: connectionData["DFSI-VERSION"],
                                  channelMode: connectionData["CHANNEL-MODE"],
                                  ctcValue:
                                    connectionData["CTC-SQUELCH-SYSTEM"],
                                  cdcValue:
                                    connectionData["CDC-SQUELCH-SYSTEM"],
                                }) ?? []
                              }
                              onChange={({
                                selectedItem,
                              }: {
                                selectedItem: string;
                              }) =>
                                setConnectionData({
                                  ...connectionData,
                                  [field.name]: selectedItem,
                                })
                              }
                              selectedItem={connectionData[field.name]}
                              tooltipText={field.tooltipText}
                              disabled={field.disabled?.(
                                connectionData["CHANNEL-MODE"],
                                connectionData["DFSI-VERSION"],
                                isChecked
                              )}
                              required
                            />
                          );
                        default:
                          return (
                            <TextField
                              onChange={(val) => {
                                set.add(field.name);
                                setConnectionData({
                                  ...connectionData,
                                  [field.name]: val,
                                });
                              }}
                              placeholder={field.label}
                              value={
                                connectionData[field.name] !== undefined
                                  ? (connectionData[field.name] as string)
                                  : "None"
                              }
                              invalid={
                                set.has(field.name) &&
                                field.isInputInvalid?.(
                                  connectionData[field.name] as string
                                )
                              }
                              invalidText={
                                field.invalidText ||
                                FORM_VALIDATION_TRANSLATIONS.required(
                                  field.label
                                )
                              }
                              labelText={field.label}
                              tooltipText={field.tooltipText}
                              required
                              disabled={field.disabled?.()}
                            />
                          );
                      }
                    })}
                  </div>
                );
              }
            )}
          </div>
        </Form>
      </div>
      <DeleteModal
        modalHeaderText={CREATE_DFSI_CHANNEL_TRANSLATIONS.deleteModal.header}
        modalDescriptionText={
          CREATE_DFSI_CHANNEL_TRANSLATIONS.deleteModal.description
        }
        onDelete={() => navigate("/configurationManagement/dfsiChannels")}
        open={open}
        setOpen={setOpen}
        confirmationButtonLabel={
          CREATE_DFSI_CHANNEL_TRANSLATIONS.deleteModal.goBackButtonLabel
        }
        cancelIcon={DeleteModalIcons.Cancel}
      />
      <div className="flex justify-end form-footer">
        <div className="mr-2">
          <Button
            className="w-full"
            type="button"
            kind="tertiary"
            disabled={false}
            label={CREATE_DFSI_CHANNEL_TRANSLATIONS.cancelButtonLabel}
            onClick={() => setOpen(true)}
            dataCy="CancelCreateDFSIChannel-"
          />
        </div>

        <div>
          <Button
            className="w-full"
            type="button"
            loading={loading}
            disabled={isFormInvalid({ data: connectionData })}
            onClick={createDFSIChannels}
            label={CREATE_DFSI_CHANNEL_TRANSLATIONS.createButtonLabel}
            dataCy="createDFSIChannel-"
          />
        </div>
      </div>
    </>
  );
};

export default NewDFSIChannel;
